import type { AbilityName, SkillName } from '@character-creator/types';

export interface PdfFieldMapping {
  fieldName: string;
  value: string | boolean;
  fieldType: 'text' | 'checkbox';
}

export interface CharacterSheetData {
  // Basic Info
  characterName: string;
  classLevel: string;
  background: string;
  playerName: string;
  race: string;
  alignment: string;
  experiencePoints: string;

  // Ability Scores (scores and modifiers)
  abilities: {
    [key in AbilityName]: {
      score: number;
      modifier: number;
    };
  };

  // Combat
  proficiencyBonus: number;
  armorClass: number;
  initiative: number;
  speed: number;
  hitPointMaximum: number;
  hitPointCurrent: string;
  hitPointTemp: string;
  hitDiceTotal: string;
  hitDice: string;

  // Proficiencies
  savingThrowProficiencies: AbilityName[];
  skillProficiencies: SkillName[];

  // Skills calculated
  skillModifiers: Record<SkillName, number>;
  savingThrowModifiers: Record<AbilityName, number>;

  // Passive Perception
  passivePerception: number;

  // Languages and other proficiencies
  proficienciesAndLanguages: string;

  // Features
  featuresAndTraits: string;

  // Equipment
  equipment: string;
  currency: {
    cp: number;
    sp: number;
    ep: number;
    gp: number;
    pp: number;
  };

  // Attacks
  attacks: {
    name: string;
    bonus: string;
    damage: string;
    damageType?: string;
  }[];

  // Personality
  personalityTraits: string;
  ideals: string;
  bonds: string;
  flaws: string;

  // Backstory & Appearance (page 2)
  backstory: string;
  appearance: {
    age: string;
    height: string;
    weight: string;
    eyes: string;
    skin: string;
    hair: string;
  };
  allies: string;
  additionalFeaturesTraits: string;
  treasure: string;

  // Spellcasting (page 3, optional)
  spellcasting?: {
    spellcastingClass: string;
    spellcastingAbility: string;
    spellSaveDC: number;
    spellAttackBonus: number;
    // Spell slots per level (index 0 = level 1, etc.)
    spellSlots: { total: number; used: number }[];
    // Spell names organized by level (0 = cantrips, 1 = level 1, etc.)
    spellsByLevel: string[][];
  };
}

export interface PdfExportOptions {
  flatten?: boolean;
  filename?: string;
  /** Template ID to use for export (e.g., 'ignus-arcana', 'srd-pragmata') */
  templateId?: string;
}

/**
 * Ignus-specific character sheet data
 * Extends the standard CharacterSheetData with Ignus ruleset fields
 */
export interface IgnusCharacterSheetData extends CharacterSheetData {
  // Override race with Ignus terminology
  species: string;
  lineage?: string;

  // Faction System
  faction: 'circle' | 'seat' | 'civil-society' | 'shunned' | null;
  factionName?: string;

  // Elemental Nature
  creatureType: string;
  restMechanic: string;
  damageResistances: string[];

  // Lineage Traits (expanded from race traits)
  lineageTraits: {
    name: string;
    description: string;
  }[];

  // Tiered Feats (Ignus feat system)
  tieredFeats: {
    name: string;
    tier: 1 | 2 | 3;
    statModifier?: {
      ability: AbilityName;
      amount: number;
    };
    benefits: string[];
  }[];

  // Innate Spellcasting (separate from class spellcasting)
  innateSpellcasting?: {
    ability: string;
    spells: {
      name: string;
      level: number;
      frequency: 'at-will' | '1/day' | '1/short-rest' | '1/long-rest';
    }[];
  };
}

export class PdfExportError extends Error {
  constructor(
    message: string,
    public readonly code: 'TEMPLATE_LOAD_FAILED' | 'FIELD_MAPPING_FAILED' | 'GENERATION_FAILED' | 'SERVER_GENERATION_FAILED' | 'CLIENT_GENERATION_FAILED',
    public readonly cause?: Error
  ) {
    super(message);
    this.name = 'PdfExportError';
  }
}
